import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button, Card, Checkbox, FormField, Input, MultiValueInput, PageHeader, Select, Textarea,
  CardRadio, RadioGroup, UnsavedGuard,
} from '../../components/shared';
import { toast } from '../../components/shared/Toast';
import {
  listAutomations, createAutomation, patchAutomation,
  WEEKDAY_LABEL, WINDOW_OPTIONS,
  type AiAutomation, type AutomationWindow, type AutomationTriggerKind, type MailEventFilter,
} from '../../api/assist-automations';
import { listMailAccounts, type MailAccount } from '../../api/external-mail';
import { listAiAgents, type AiAgent } from '../../api/assist-agents';
import { AI_AUTOMATIONS_PATH } from '../../lib/ai-menu';

/**
 * 업무자동화 만들기 · 고치기 — **주소를 갖는 자리** (2026-09-12).
 *
 * ## ★★★ 왜 목록 «안»에서 꺼냈나
 *
 * 에이전트 폼이 먼저 같은 길을 갔다(`AiAgentFormPage`). 자동화 폼만 목록 안 인라인으로 남아
 * 있어서, 같은 메뉴 안의 두 화면이 **다른 물건처럼** 굴었다 — 링크를 나눌 수 없고, 뒤로가기가
 * 목록이 아니라 AI 메뉴 밖으로 나가고, 새로고침하면 적던 것이 통째로 사라졌다.
 * ⭑ 주소를 가지면 **나가기 전에 묻는 규칙**(정본 P13)이 그대로 걸린다. 그것이 이 판의 알맹이다.
 *
 * ## ★★★ 공통 부품으로 갈아탔다 — 그래서 CSS 를 한 줄도 안 고쳤다
 *
 * 인라인 폼은 날것 `<input|textarea|select>` 를 썼고, AI 셸의 새 결(`AiOa.css`)은 그 칸들을
 * **상자 이름으로** 하나씩 집어 밑줄을 그리고 있었다(`.aiauto__fld > input` …). 상자가 한 겹만
 * 늘어도 그 칸이 옛 결로 남는 자리라, 리뷰가 두 번 잡았던 모양이다.
 * ⭑ 여기서는 에이전트 폼과 **같은 부품·같은 스코프**(`.aiform`)를 쓴다. `AiOa.css` 가 이미
 *   `.aiform .wc-input` · `.wc-textarea` · `.wc-combobox__trigger` 를 덮고 있어 **새 규칙이
 *   필요 없다** — 결이 갈라질 자리 자체가 없어진다.
 * ⭑ 그래서 판 12 에서 「이 폼의 다른 칸이 네이티브라」 네이티브 `<select>` 를 골랐던 근거도
 *   사라졌다. 이제 정본 `Select` 를 쓴다.
 *
 * ## ★★ 부류는 만들 때만 고른다
 *
 * 「때가 되면」과 「새 메일이 오면」은 **필요한 칸이 서로 없다.** 고치는 중에 바꾸면 반대편 칸이
 * 남아 「일요일 오전에 도는 메일 자동화」 같은 줄이 생기므로, 서버도 안 받고 여기서도 안 그린다.
 */

interface Draft {
  name: string;
  triggerKind: AutomationTriggerKind;
  weekday: number;
  window: AutomationWindow;
  accountIds: string[];
  from: string[];
  hasAttachment: boolean;
  subjectContains: string;
  prompt: string;
  /** 붙일 에이전트. '' = 안 붙임(그냥 묻는다). */
  agentId: string;
}

const EMPTY: Draft = {
  name: '', triggerKind: 'SCHEDULE', weekday: 1, window: 'MORNING',
  accountIds: [], from: [], hasAttachment: false, subjectContains: '', prompt: '', agentId: '',
};

/** 사건 조건 — **빈 것은 안 담는다**(빈 배열·빈 글자를 담으면 조건이 있는 것처럼 보인다). */
export function eventFilterOf(d: Pick<Draft, 'accountIds' | 'from' | 'hasAttachment' | 'subjectContains'>): MailEventFilter {
  const f: MailEventFilter = {};
  if (d.accountIds.length > 0) f.accountIds = d.accountIds;
  if (d.from.length > 0) f.from = d.from;
  if (d.hasAttachment) f.hasAttachment = true;
  if (d.subjectContains.trim()) f.subjectContains = d.subjectContains.trim();
  return f;
}

/** 이미 있는 자동화를 폼 모양으로. */
function draftOf(a: AiAutomation): Draft {
  return {
    ...EMPTY,
    name: a.name,
    triggerKind: a.triggerKind,
    weekday: a.weekday ?? 1,
    window: a.window ?? 'MORNING',
    accountIds: a.eventFilter?.accountIds ?? [],
    from: a.eventFilter?.from ?? [],
    hasAttachment: a.eventFilter?.hasAttachment === true,
    subjectContains: a.eventFilter?.subjectContains ?? '',
    prompt: a.prompt,
    /* 중지된 것이 붙어 있으면 고르기 목록에 없어 칸이 비어 보이는데, 저장할 때 떼어 준다. */
    agentId: a.agentId ?? '',
  };
}

export function AiAutomationFormPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [initial, setInitial] = useState<Draft>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  /* 폼을 그리는 데 필요한 것들 — 서버가 준다(화면이 숫자를 따로 적으면 언젠가 어긋난다). */
  const [timezone, setTimezone] = useState<string | null>(null);
  const [bucketMin, setBucketMin] = useState(10);
  const [runsPerDay, setRunsPerDay] = useState(24);
  const [accounts, setAccounts] = useState<MailAccount[]>([]);
  const [agents, setAgents] = useState<AiAgent[]>([]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const r = await listAutomations();
        if (!alive) return;
        setTimezone(r.timezone);
        if (typeof r.eventBucketMinutes === 'number') setBucketMin(r.eventBucketMinutes);
        if (typeof r.eventRunsPerDayMax === 'number') setRunsPerDay(r.eventRunsPerDayMax);

        if (isNew) {
          /* 시간대가 없는 회사에서는 고를 수 있는 쪽으로 열어 준다. */
          const seed = { ...EMPTY, triggerKind: (r.timezone ? 'SCHEDULE' : 'EVENT') as AutomationTriggerKind };
          setDraft(seed);
          setInitial(seed);
        } else {
          /* ⚠ 자동화에는 «한 건만» 읽는 창구가 없다 — 목록에서 찾는다. */
          const found = r.automations.find((a) => a.id === id);
          if (!found) { setFailed('자동화를 찾을 수 없습니다.'); return; }
          const seed = draftOf(found);
          setDraft(seed);
          setInitial(seed);
        }
      } catch {
        if (alive) setFailed('불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id, isNew]);

  useEffect(() => {
    void listMailAccounts().then(setAccounts).catch(() => { /* 덤이다 — 없으면 고르기만 안 뜬다. */ });
    void listAiAgents('all')
      .then((rows) => setAgents(rows.filter((g) => g.status === 'ACTIVE')))
      .catch(() => { /* 덤이다 — 없으면 고르기만 안 뜬다. */ });
  }, []);

  /**
   * ★★★ **「비어 있지 않다」가 아니라 「사람이 고쳤다」**(정본 P13). 새로 열자마자 뜨는 창은
   *    사람이 읽지 않고 누르게 만든다 — 그러면 정작 잃을 것이 있을 때도 그냥 누른다.
   */
  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(initial),
    [draft, initial],
  );

  const set = <K extends keyof Draft>(k: K, v: Draft[K]): void =>
    setDraft((d) => ({ ...d, [k]: v }));

  const canSchedule = timezone !== null;

  async function save(): Promise<void> {
    if (savingRef.current) return;
    const name = draft.name.trim();
    const prompt = draft.prompt.trim();
    if (!name) { toast.error('이름을 입력해 주세요.'); return; }
    if (!prompt) { toast.error('무엇을 물을지 적어 주세요.'); return; }

    savingRef.current = true;
    setSaving(true);
    try {
      const common = { name, prompt };
      /*
       * ★★★ **고를 수 없는 id 는 «안 붙임»으로 보낸다.** 중지된 에이전트가 붙어 있으면 고르기
       *    목록(살아 있는 것만)에 없어 칸은 비어 보이는데 상태에는 그 id 가 남는다 — 그대로
       *    보내면 서버가 400 으로 막아 이름만 고치려 해도 저장이 통째로 막힌다.
       * ★★ **고르기 칸이 서지도 않았으면 손대지 않는다** — 목록을 못 읽으면 칸이 안 그려지는데,
       *    그때 null 을 보내면 이름만 고쳤는데 붙어 있던 에이전트가 조용히 떨어진다.
       */
      const picker = agents.length > 0;
      const usable = draft.agentId !== '' && agents.some((g) => g.id === draft.agentId);
      const agentField = picker ? { agentId: usable ? draft.agentId : null } : {};
      const shape = draft.triggerKind === 'EVENT'
        ? { eventFilter: eventFilterOf(draft) }
        : { weekday: draft.weekday, window: draft.window };

      if (isNew) {
        /* ★ 부류는 **만들 때만** 보낸다 — 고치기에서는 서버가 안 받는다. */
        await createAutomation({
          ...common, ...shape, ...agentField, triggerKind: draft.triggerKind,
          ...(draft.triggerKind === 'EVENT' ? { eventKind: 'mail.received' as const } : {}),
        });
      } else {
        await patchAutomation(id!, { ...common, ...shape, ...agentField });
      }
      /* ★ 저장에 성공한 «한 곳»에서 경고를 끈다 — 안 끄면 돌아가는 길에 한 번 더 묻는다. */
      setInitial(draft);
      navigate(AI_AUTOMATIONS_PATH);
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg ?? '저장하지 못했습니다.');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  if (failed !== null) {
    return (
      <div className="aiagents">
        <PageHeader breadcrumb={['AI', '업무자동화']} title="업무자동화" />
        <div className="aiagents__col">
          <p className="aiagents__note" role="alert">{failed}</p>
          <Button variant="secondary" onClick={() => navigate(AI_AUTOMATIONS_PATH)}>목록으로</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="aiagents">
      <UnsavedGuard when={dirty && !saving} />
      <PageHeader
        breadcrumb={['AI', '업무자동화']}
        title={isNew ? '자동화 만들기' : '자동화 고치기'}
      />
      <div className="aiagents__col">
        {loading ? (
          <p className="aiagents__note">불러오는 중입니다…</p>
        ) : (
          <form
            className="aiform"
            onSubmit={(e) => { e.preventDefault(); void save(); }}
          >
            <Card title="기본 정보" className="aiform__sec">
              <FormField label="이름" required>
                <Input
                  value={draft.name}
                  onChange={(e) => set('name', e.target.value)}
                  placeholder="주간 근태 요약"
                  maxLength={120}
                />
              </FormField>

              {/* ★★ 부류는 **만들 때만** 고른다 — 고치는 중에는 안 보여 준다(서버도 안 받는다). */}
              {isNew && (
                <RadioGroup legend="언제 도는가" name="aiauto-trigger">
                  <CardRadio
                    value="SCHEDULE"
                    checked={draft.triggerKind === 'SCHEDULE'}
                    disabled={!canSchedule}
                    onChange={() => set('triggerKind', 'SCHEDULE')}
                    title="정해진 때에"
                    description={canSchedule
                      ? '매주 고른 요일의 오전·오후·저녁에 한 번 돕니다.'
                      : '회사 시간대가 정해져 있지 않아 지금은 고를 수 없습니다.'}
                  />
                  <CardRadio
                    value="EVENT"
                    checked={draft.triggerKind === 'EVENT'}
                    onChange={() => set('triggerKind', 'EVENT')}
                    title="새 메일이 왔을 때"
                    description={`연동해 둔 메일 계정으로 새 메일이 들어오면 돕니다. ${bucketMin}분 안에 온 메일은 한 번에 묶어 요약합니다.`}
                  />
                </RadioGroup>
              )}
            </Card>

            {draft.triggerKind === 'SCHEDULE' ? (
              <Card title="언제 도는가" className="aiform__sec">
                <FormField label="요일">
                  <Select
                    ariaLabel="요일"
                    value={String(draft.weekday)}
                    onChange={(v) => set('weekday', Number(v))}
                    options={WEEKDAY_LABEL.map((w, i) => ({ value: String(i), label: `매주 ${w}요일` }))}
                  />
                </FormField>
                <FormField label="시간대">
                  <Select
                    ariaLabel="시간대"
                    value={draft.window}
                    onChange={(v) => set('window', v as AutomationWindow)}
                    options={WINDOW_OPTIONS.map((w) => ({ value: w.value, label: `${w.label} (${w.hint})` }))}
                  />
                </FormField>
                {/* ★★★ 정각을 안 묻는 이유를 적는다 — 안 적으면 「9시로 맞추고 싶은데」가 된다. */}
                <p className="aiform__note">
                  정각이 아니라 <b>그 시간대 안</b>에서 돕니다. 한꺼번에 몰리면 모두가 느려지기 때문입니다.
                  {timezone && <> 회사 시간대(<b>{timezone}</b>)로 셉니다.</>}
                </p>
              </Card>
            ) : (
              <Card title="어떤 메일이 깨우나" className="aiform__sec">
                {/*
                  ★★★ **「어디로 왔나」를 «맨 위»에 둔다** (2026-09-11 사용자 실사용에서 드러났다).
                    「naver 로 메일이 들어오면」을 표현할 칸이 없어, 사람들이 그 주소를
                    «보낸사람» 칸에 적는다 — 같은 주소라 맞아 보이지만 **뜻이 정반대**다.
                  ★ 연동한 계정이 없으면 이 묶음은 안 그린다 — 고를 것이 없는 칸은 세우지 않는다.
                */}
                {accounts.length > 0 && (
                  /*
                   * ⚠ **`FormField` 로 감싸지 않는다.** 감쌌더니 바깥 라벨이 안쪽 체크박스들의
                   *   접근성 이름을 덮어써서, 읽어 주는 프로그램이 **어느 계정인지 말하지 못했다**
                   *   (2026-09-12 검사가 잡았다 — 「이름으로 못 찾는다」). 묶음 이름은 `role="group"`
                   *   이 지고, 칸 하나하나는 제 주소를 이름으로 갖는다.
                   */
                  <div className="aiform__doms" role="group" aria-label="어느 사서함으로 온 것만">
                    <p className="aiform__note">
                      <b>어느 사서함</b>으로 온 것만 (안 고르면 전부)
                    </p>
                    {accounts.map((acc) => (
                      <Checkbox
                        key={acc.id}
                        checked={draft.accountIds.includes(acc.id)}
                        onChange={(e) => set(
                          'accountIds',
                          e.target.checked
                            ? [...draft.accountIds, acc.id]
                            : draft.accountIds.filter((x) => x !== acc.id),
                        )}
                        label={acc.email}
                      />
                    ))}
                  </div>
                )}

                <FormField label="보낸사람 주소">
                  {/* ★ 주소는 소문자로 씻어 담는다 — 「@naver.com」처럼 적으면 그 도메인 전체다. */}
                  <MultiValueInput
                    values={draft.from}
                    onChange={(next) => set('from', next)}
                    normalize={(raw) => raw.trim().toLowerCase()}
                    max={20}
                    placeholder="누가 «보낸» 것만 (비워 두면 전부)"
                    ariaLabel="보낸사람 주소"
                    removeLabel="{v} 빼기"
                  />
                </FormField>

                <FormField label="제목에 들어갈 말">
                  <Input
                    value={draft.subjectContains}
                    onChange={(e) => set('subjectContains', e.target.value)}
                    placeholder="이 말이 들어갈 때 (비워 두면 전부)"
                    maxLength={100}
                  />
                </FormField>

                <Checkbox
                  checked={draft.hasAttachment}
                  onChange={(e) => set('hasAttachment', e.target.checked)}
                  label="첨부가 있는 메일만"
                />

                {/* ★★ 무엇이 AI 에게 가는지 **적는다** — 개인 외부 메일도 포함된다(설계 §10-01). */}
                <p className="aiform__note">
                  적은 조건을 <b>모두</b> 만족하는 메일이 깨웁니다. 아무것도 안 적으면 <b>내 새 메일 전부</b>입니다.
                  {' '}<b>사서함</b>은 「어디로 왔나」이고 <b>보낸사람</b>은 「누가 보냈나」입니다 — 뜻이 다릅니다.
                  보낸사람·제목·첨부 이름이 AI 에게 전달되고, 본문과 첨부의 글은 AI 가 필요할 때 읽습니다 —
                  <b>연동해 둔 개인 외부 메일도 포함</b>됩니다. 메일이 몰려도 하루 <b>{runsPerDay}회</b>까지만 돕니다.
                </p>
              </Card>
            )}

            <Card title="무엇을 물을까" className="aiform__sec">
              <FormField label="묻는 말" required>
                <Textarea
                  value={draft.prompt}
                  onChange={(e) => set('prompt', e.target.value)}
                  placeholder="내 이번 주 근태를 요약해줘. 지각·초과근무·잔여 연차를 알려줘."
                  rows={3}
                  maxLength={2000}
                />
              </FormField>
              <p className="aiform__note">대화에서 묻듯이 적으면 됩니다.</p>
            </Card>

            {/*
              * ★★ 에이전트 — **안 고르면 그냥 묻는다.**
              * ★ 고를 것이 하나도 없으면 **묶음을 아예 안 그린다** — 빈 고르기를 세워 두면
              *   「여기서 무엇을 골라야 하나」를 먼저 묻게 된다.
              */}
            {agents.length > 0 && (
              <Card title="에이전트" className="aiform__sec">
                <FormField label="붙일 에이전트">
                  <Select
                    ariaLabel="에이전트"
                    value={draft.agentId}
                    onChange={(v) => set('agentId', v)}
                    options={[
                      { value: '', label: '안 붙임 — 그냥 묻습니다' },
                      ...agents.map((g) => ({ value: g.id, label: g.name })),
                    ]}
                  />
                </FormField>
                <p className="aiform__note">
                  붙이면 그 에이전트의 <b>역할·답변 규칙·검색 범위</b>로 돕니다.
                  {draft.agentId !== '' && !agents.some((g) => g.id === draft.agentId) && (
                    <>
                      {' '}
                      <b>지금 붙어 있는 에이전트는 중지되어 고를 수 없습니다</b> — 이대로 저장하면
                      떨어집니다(이름·묻는 말만 고쳐도 됩니다).
                    </>
                  )}
                </p>
              </Card>
            )}

            <div className="aiform__acts">
              <Button type="submit" variant="primary" loading={saving}>
                {isNew ? '만들기' : '저장'}
              </Button>
              <Button type="button" variant="secondary" onClick={() => navigate(AI_AUTOMATIONS_PATH)}>
                취소
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
